import { useRouter } from 'next/router';
import { linkifySegments } from '../lib/linkify';
import { useBasePath } from '../lib/use-base-path';
import { resolveInAppPath } from '../lib/app-link';
import { mediaFileLink } from '../lib/media-file-link';
import { openDocViewer } from '../lib/doc-viewer-presenter';

// Plain-text body with bare URLs rendered as real links — the counterpart of `Markdown` for the
// surfaces that are NOT markdown: chat bubbles that never go through the markdown pipeline (user
// turns, WhatsApp group member turns, voice transcripts) and model-authored prose fields such as a
// task's description or result.
//
// Deliberately emits no wrapper element: it returns an inline fragment so a call site keeps whatever
// block it already renders (and keeps owning `dir` — see `lib/message-direction.ts`, Golden Rule 13).
//
// Only `http(s)` runs are linkified (`lib/linkify.ts`), so a script- or inline-data scheme in
// model-supplied text can never become an activatable link here. An `https://` link that points at one
// of the app's own destinations is routed in-app instead of opening a tab, exactly as `Markdown` does.
//
// Native mirrors: `LinkifiedText` (iOS `Features/Chat/LinkifiedText.swift`, Android
// `features/LinkifiedText.kt`).
//
// A bare URL to a FILE on Wix media (`lib/media-file-link.ts`) opens in the in-app doc viewer on a plain
// click — the raw tab shows a charset-less Hebrew text file as mojibake — and still opens the tab on a
// modified or middle click, because it stays a real anchor.
export function LinkifiedText({ text }: { text: string }) {
  const router = useRouter();
  const basePath = useBasePath();
  const segments = linkifySegments(text);
  return (
    <>
      {segments.map((s, i) => {
        if (s.kind !== 'url') return <span key={i}>{s.value}</span>;
        const inApp = resolveInAppPath(s.value);
        const target = inApp ? `${basePath}${inApp}` : s.value;
        const media = inApp ? null : mediaFileLink(s.value);
        return (
          <a
            key={i}
            href={target}
            {...(inApp ? {} : { target: '_blank', rel: 'noreferrer' })}
            onClick={
              inApp
                ? (e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    // A link inside a row that navigates on click must not also trigger the row.
                    e.stopPropagation();
                    void router.push(inApp, target);
                  }
                : media
                  ? (e) => {
                      e.stopPropagation();
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                      e.preventDefault();
                      openDocViewer({
                        file: { name: media.fileName, isApp: false, content: media.url },
                        kind: media.kind,
                        pageName: 'document_viewer',
                      });
                    }
                  : (e) => e.stopPropagation()
            }
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              wordBreak: 'break-all',
            }}
          >
            {s.value}
          </a>
        );
      })}
    </>
  );
}
